import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AgentStepTimeline, type AgentStep } from "../../components/AIAgents/ChatWithAgent/components/AgentStepTimeline";
import { ChatInputArea, type ChatComposerHandle } from "../../components/AIAgents/ChatWithAgent/components/ChatInputArea";
import { MessageBubble } from "../../components/AIAgents/ChatWithAgent/components/MessageBubble";
import { MarkdownRenderer } from "../../components/AIAgents/ChatWithAgent/components/MarkdownRenderer";
import type { QuickStartValues } from "./QuickStartForm";
import type { BrandDetailsDraft, CreatedAgent } from "./quickStartOnboarding";
import { LogoUpload } from "../brand/components/LogoUpload";
import { BrandColorPicker } from "../brand/components/BrandColorPicker";
import { sanitizeLogoUrlForSave } from "./brandDetailsFromProfile";

/**
 * The setup chat's look, shared by the assistant-led chat and the scripted one
 * so nothing changes style at the hand-over. Matches agent chat: the owner's
 * messages in a quiet bordered box on the right, the assistant's as plain text
 * under its name (no bubble), the steps it took above its words, and the
 * agent-chat composer.
 */

export interface ThreadBubble {
  from: "assistant" | "user";
  text: string;
  /** What the assistant did before this reply (tool calls), shown above it. */
  steps?: AgentStep[];
  /** Shows the profile card under the text (the confirm step). */
  summary?: QuickStartValues;
  /** The opening menu under the text. */
  options?: SetupOption[];
  /** The team, as cards, once setup has created it. */
  team?: CreatedAgent[];
  /** The Brand details the owner confirmed in step 2 (SGL-512). */
  details?: BrandDetailsDraft;
}

/** Claude-style assistant turn: the logo in a column on the left, the
 *  content beside it. While the assistant works the logo gently rocks and
 *  breathes; it settles when the reply arrives. */
const LOGO_COL = "w-10";

const AssistantRow: React.FC<{
  avatar: React.ReactNode;
  showAvatar: boolean;
  working?: boolean;
  children: React.ReactNode;
}> = ({ avatar, showAvatar, working = false, children }) => (
  <div className="flex w-full gap-3.5">
    <div className={`${LOGO_COL} shrink-0`}>
      {showAvatar && (
        <span className={`block ${working ? "sagelyn-logo-working" : ""}`} aria-hidden>
          {avatar}
        </span>
      )}
    </div>
    <div className="flex min-w-0 flex-1 flex-col gap-3 pt-[7px]">{children}</div>
    <style>{`
      @keyframes sagelynLogoThink {
        0%, 100% { transform: rotate(0deg) scale(1); }
        25% { transform: rotate(-14deg) scale(0.92); }
        50% { transform: rotate(0deg) scale(1.05); }
        75% { transform: rotate(14deg) scale(0.92); }
      }
      .sagelyn-logo-working { animation: sagelynLogoThink 1.6s ease-in-out infinite; transform-origin: 50% 50%; }
      @media (prefers-reduced-motion: reduce) { .sagelyn-logo-working { animation: none; } }
    `}</style>
  </div>
);

export const ProfileSummaryCard: React.FC<{ values: QuickStartValues }> = ({ values }) => {
  const place = [
    values.city,
    [values.state, values.zip].filter(Boolean).join(" "),
    values.country && values.country !== "United States" ? values.country : "",
  ]
    .filter(Boolean)
    .join(", ");
  const rows: { icon: string; label: string; value: string }[] = [
    { icon: "storefront", label: "Business", value: values.businessName },
    { icon: "place", label: "Location", value: place },
    { icon: "handyman", label: "Services", value: values.services.length ? values.services.join(", ") : "Not added yet" },
    { icon: "language", label: "Website", value: values.website || "None" },
  ];
  return (
    <div
      className="overflow-hidden rounded-2xl border border-theme-border bg-theme-surface shadow-[0_2px_12px_rgba(15,23,42,0.06)]"
      style={{ animation: "setupCardIn 0.3s ease both" }}
    >
      <div className="flex items-center gap-2 border-b border-theme-border px-5 py-3">
        <span className="material-icons text-[18px] text-theme-primary">badge</span>
        <span className="text-[13px] font-medium text-theme-fg-muted">Your business profile</span>
      </div>
      <dl className="divide-y divide-theme-border">
        {rows.map((r) => (
          <div key={r.label} className="flex items-start gap-3 px-5 py-3">
            <span className="material-icons mt-0.5 text-[18px] text-theme-fg-muted">{r.icon}</span>
            <dt className="w-20 shrink-0 text-[13px] text-theme-fg-muted">{r.label}</dt>
            <dd className="min-w-0 flex-1 break-words text-[14px] text-theme-fg">{r.value}</dd>
          </div>
        ))}
      </dl>
      <style>{`@keyframes setupCardIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }`}</style>
    </div>
  );
};

/** A choice offered in the opening message; one click acts on it. */
export interface SetupOption {
  key: string;
  label: string;
  description?: string;
  icon: string;
}

/** The opening menu: the original onboarding entry buttons (same card,
 *  icon tile, label and description), stacked vertically. */
const OptionList: React.FC<{
  options: SetupOption[];
  onPick?: (key: string) => void;
  disabled?: boolean;
}> = ({ options, onPick, disabled }) => (
  <div className="w-full">
    <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-3">
    {options.map((o) => (
      <button
        key={o.key}
        type="button"
        disabled={disabled}
        onClick={() => onPick?.(o.key)}
        className="group flex w-full items-center gap-3.5 rounded-2xl border border-theme-accent-warm/40 bg-theme-surface p-4 text-left shadow-[0_18px_36px_-30px_rgba(0,0,0,0.52)] transition hover:-translate-y-0.5 hover:border-theme-accent-warm hover:shadow-[0_24px_48px_-32px_rgba(0,0,0,0.6)] disabled:pointer-events-none disabled:opacity-50"
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-surface-ui-hover text-theme-accent-warm">
          <span className="material-icons text-[18px]">{o.icon}</span>
        </span>
        <span className="flex min-w-0 flex-col">
          <span className="text-[14px] font-semibold leading-tight text-theme-fg">{o.label}</span>
          {o.description && (
            <span className="mt-1 text-[11px] font-medium leading-snug text-theme-fg-muted">{o.description}</span>
          )}
        </span>
      </button>
    ))}
    </div>
    <p className="mt-4 text-[13px] text-theme-fg-muted">Something else? Just type it below.</p>
  </div>
);

/** "Change something": the same card, every field editable in place. Only
 *  the fields that are wrong get touched; nothing restarts. */
export const EditableProfileCard: React.FC<{
  values: QuickStartValues;
  onSave: (v: QuickStartValues) => void;
  onCancel: () => void;
}> = ({ values, onSave, onCancel }) => {
  const [v, setV] = useState<QuickStartValues>(values);
  const [services, setServices] = useState(values.services.join(", "));
  const missing = !v.businessName.trim() || !v.city.trim() || !v.state.trim();
  const field = (
    icon: string,
    label: string,
    value: string,
    onChange: (x: string) => void,
    placeholder: string,
    required = false,
  ) => (
    <label className="flex items-center gap-3 px-5 py-2.5">
      <span className="material-icons mt-0.5 text-[18px] text-theme-fg-muted" aria-hidden>{icon}</span>
      <span className="w-20 shrink-0 text-[13px] text-theme-fg-muted">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="min-w-0 flex-1 rounded-lg border border-theme-border bg-theme-background px-3 py-2 text-[14px] text-theme-fg outline-none placeholder:text-slate-400 focus:border-theme-fg-muted/50"
      />
    </label>
  );
  return (
    <div className="overflow-hidden rounded-2xl border border-theme-border bg-theme-surface shadow-[0_2px_12px_rgba(15,23,42,0.06)]">
      <div className="flex items-center gap-2 border-b border-theme-border px-5 py-3">
        <span className="material-icons text-[18px] text-theme-primary" aria-hidden>edit</span>
        <span className="text-[13px] font-medium text-theme-fg-muted">Edit your business profile</span>
      </div>
      <div className="flex flex-col py-2">
        {field("storefront", "Business", v.businessName, (x) => setV({ ...v, businessName: x }), "Your business name", true)}
        {field("location_city", "City", v.city, (x) => setV({ ...v, city: x }), "e.g. Austin", true)}
        {field("map", "State", v.state, (x) => setV({ ...v, state: x }), "State, province or region", true)}
        {field("markunread_mailbox", "Zip code", v.zip, (x) => setV({ ...v, zip: x }), "e.g. 78626")}
        {field("public", "Country", v.country, (x) => setV({ ...v, country: x }), "United States")}
        {field("handyman", "Services", services, setServices, "Separated by commas")}
        {field("language", "Website", v.website, (x) => setV({ ...v, website: x }), "yourbusiness.com")}
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-theme-border px-5 py-3">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-full border border-theme-border bg-theme-surface px-4 py-2 text-sm text-theme-fg hover:border-theme-fg-muted/40"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={missing}
          onClick={() =>
            onSave({
              ...v,
              businessName: v.businessName.trim(),
              city: v.city.trim(),
              state: v.state.trim(),
              zip: v.zip.trim(),
              country: v.country.trim(),
              website: v.website.trim(),
              services: services.split(",").map((x) => x.trim()).filter(Boolean).slice(0, 12),
            })
          }
          className="rounded-full bg-brand-primary px-4 py-2 text-sm font-medium text-white hover:bg-brand-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          Save
        </button>
      </div>
    </div>
  );
};

const initialsOf = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "?";

/** The logo, or the Brand's initials when there is none: never a broken image. */
const LogoMark: React.FC<{ url: string; name: string; dark?: boolean }> = ({ url, name, dark }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [url]);
  return url && !broken ? (
    <img src={url} alt={`${name} logo`} onError={() => setBroken(true)} className={`h-14 w-14 rounded-xl border border-theme-border object-contain p-1 ${dark ? "bg-neutral-900" : "bg-white"}`} />
  ) : (
    <span aria-label={`${name} initials`} className={`flex h-14 w-14 items-center justify-center rounded-xl text-[18px] font-semibold ${dark ? "bg-neutral-900 text-neutral-400" : "bg-surface-ui-hover text-theme-fg-muted"}`}>
      {initialsOf(name)}
    </span>
  );
};

const textareaClass =
  "min-w-0 w-full rounded-lg border border-theme-border bg-theme-background px-3 py-2 text-[14px] text-theme-fg outline-none placeholder:text-slate-400 focus:border-theme-fg-muted/50";

/**
 * Step 2, "Your brand details" (SGL-512): what the website read found, every
 * field editable. The logo can be replaced by upload; a missing one shows the
 * initials. Nothing here is required: confirming saves whatever is filled.
 */
export const BrandDetailsCard: React.FC<{
  businessName: string;
  customerId: string;
  details: BrandDetailsDraft;
  /** Why the website's logo isn't shown, when it couldn't be copied. */
  logoError?: string;
  busy?: boolean;
  onConfirm: (d: BrandDetailsDraft) => void;
}> = ({ businessName, customerId, details, logoError, busy, onConfirm }) => {
  const [d, setD] = useState<BrandDetailsDraft>(details);
  const row = (icon: string, label: string, body: React.ReactNode) => (
    <div className="flex items-start gap-3 px-5 py-3">
      <span className="material-icons mt-0.5 text-[18px] text-theme-fg-muted" aria-hidden>{icon}</span>
      <span className="mt-0.5 w-24 shrink-0 text-[13px] text-theme-fg-muted">{label}</span>
      <div className="min-w-0 flex-1">{body}</div>
    </div>
  );
  return (
    <div
      className="overflow-hidden rounded-2xl border border-theme-border bg-theme-surface shadow-[0_2px_12px_rgba(15,23,42,0.06)]"
      style={{ animation: "setupCardIn 0.3s ease both" }}
      aria-label="Your brand details"
    >
      <div className="flex items-center gap-2 border-b border-theme-border px-5 py-3">
        <span className="material-icons text-[18px] text-theme-primary" aria-hidden>palette</span>
        <span className="text-[13px] font-medium text-theme-fg-muted">Your brand details</span>
      </div>
      <div className="flex flex-col divide-y divide-theme-border">
        {row("image", "Logo for light backgrounds", (
          <div className="flex flex-col gap-2">
            <LogoMark url={d.logoUrl} name={businessName} />
            {logoError && !d.logoUrl && <p className="text-[12px] text-amber-700" role="alert">{logoError}</p>}
            <LogoUpload
              logoUrl={d.logoUrl}
              logoMediaId={d.logoMediaId}
              onLogoChange={(url, mediaId) => setD({ ...d, logoUrl: sanitizeLogoUrlForSave(url), logoMediaId: mediaId })}
              customerId={customerId}
              label={d.logoUrl ? "Replace logo" : "Upload a logo"}
              inputId="setup-logo-upload"
            />
          </div>
        ))}
        {row("dark_mode", "Logo for dark backgrounds", (
          <div className="flex flex-col gap-2">
            {/* SGL-547: copied or made from the light logo; the owner can replace it. */}
            <LogoMark url={d.logoUrlDark} name={businessName} dark />
            <LogoUpload
              logoUrl={d.logoUrlDark}
              logoMediaId={d.logoMediaIdDark}
              onLogoChange={(url, mediaId) => setD({ ...d, logoUrlDark: sanitizeLogoUrlForSave(url), logoMediaIdDark: mediaId })}
              customerId={customerId}
              label={d.logoUrlDark ? "Replace" : "Upload a version for dark backgrounds"}
              inputId="setup-logo-upload-dark"
              previewOn="dark"
            />
          </div>
        ))}
        {row("format_color_fill", "Colours", (
          <div className="flex flex-col gap-2">
            {/* Detected colours are labelled as detected (SGL-223). */}
            {d.colors.length > 0 && d.colorsDetected && (
              <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Colours detected from your website. Please check them.
              </p>
            )}
            <BrandColorPicker colors={d.colors} onColorsChange={(colors) => setD({ ...d, colors })} maxColors={4} />
          </div>
        ))}
        {row("short_text", "Tagline", (
          <input value={d.tagline} onChange={(e) => setD({ ...d, tagline: e.target.value })} placeholder="Optional" className={textareaClass} />
        ))}
        {row("record_voice_over", "Brand voice", (
          <textarea rows={2} value={d.brandVoice} onChange={(e) => setD({ ...d, brandVoice: e.target.value })} placeholder="e.g. Friendly, plain-spoken, local" className={textareaClass} />
        ))}
        {row("groups", "Audience", (
          <textarea rows={2} value={d.targetAudience} onChange={(e) => setD({ ...d, targetAudience: e.target.value })} placeholder="Who you want more of" className={textareaClass} />
        ))}
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-theme-border px-5 py-3">
        <button
          type="button"
          disabled={busy}
          onClick={() => onConfirm(d)}
          className="rounded-full bg-brand-primary px-4 py-2 text-sm font-medium text-white hover:bg-brand-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          Save and set up my team
        </button>
      </div>
    </div>
  );
};

/** The confirmed Brand details, as they stay in the thread. */
export const BrandDetailsSummaryCard: React.FC<{ details: BrandDetailsDraft }> = ({ details }) => (
  <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-theme-border bg-theme-surface px-5 py-3 shadow-[0_2px_12px_rgba(15,23,42,0.06)]">
    {details.logoUrl && <img src={details.logoUrl} alt="Logo" className="h-10 w-10 rounded-lg border border-theme-border bg-white object-contain p-0.5" />}
    {details.colors.map((c) => (
      <span key={`${c.type}-${c.value}`} title={c.value} className="h-6 w-6 rounded-full border border-theme-border" style={{ background: c.value }} />
    ))}
    <span className="min-w-0 flex-1 text-[13px] text-theme-fg-muted">
      {[details.tagline, details.brandVoice].filter(Boolean).join(" · ") || "Brand details saved"}
    </span>
  </div>
);

/** The team that was just created: one card per agent, side by side. */
export const TeamCards: React.FC<{
  agents: CreatedAgent[];
  onChat?: (agentId: string) => void;
  onEdit?: (agentId: string) => void;
}> = ({ agents, onChat, onEdit }) => (
  <div className="flex flex-col gap-3">
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {agents.map((a, i) => (
        <div
          key={a.id}
          className="flex flex-col items-center rounded-2xl border border-theme-border bg-theme-surface px-4 pb-4 pt-5 text-center shadow-[0_2px_12px_rgba(15,23,42,0.06)]"
          style={{ animation: `setupCardIn 0.35s ease ${i * 0.08}s both` }}
        >
          <img src={a.avatarUrl} alt="" className="h-16 w-16 rounded-full" />
          <p className="mt-3 text-[15px] font-medium text-theme-fg">{a.firstName}</p>
          <p className="mt-0.5 text-[13px] text-theme-fg-muted">{a.role}</p>
          <div className="mt-4 flex w-full items-center gap-2">
            <button
              type="button"
              onClick={() => onChat?.(a.id)}
              className="flex-1 rounded-full bg-brand-primary px-3 py-2 text-[13px] font-medium text-white hover:bg-brand-primary-hover"
            >
              Chat with {a.firstName}
            </button>
            <button
              type="button"
              onClick={() => onEdit?.(a.id)}
              aria-label={`Edit ${a.firstName}`}
              title={`Edit ${a.firstName}'s name, picture and instructions`}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-theme-border text-theme-fg-muted hover:border-theme-fg-muted/40 hover:text-theme-fg"
            >
              <span className="material-icons-outlined text-[17px]" aria-hidden>edit</span>
            </button>
          </div>
        </div>
      ))}
    </div>
    <p className="text-[13px] text-theme-fg-muted">
      You can change any name, picture or instructions anytime with the edit button.
    </p>
  </div>
);

export const SetupThread: React.FC<{
  bubbles: ThreadBubble[];
  avatar: React.ReactNode;
  /** Opening-menu clicks; the menu is live only while it is the last message. */
  onOption?: (key: string) => void;
  onChatAgent?: (agentId: string) => void;
  onEditAgent?: (agentId: string) => void;
}> = ({ bubbles, avatar, onOption, onChatAgent, onEditAgent }) => {
  return (
  <>
    {bubbles.map((b, i) => {
      if (b.from === "user") {
        // Agent chat's own message component: identical owner messages.
        return <MessageBubble key={i} message={{ role: "user", content: b.text }} />;
      }
      if (b.options) {
        return (
          <AssistantRow key={i} avatar={avatar} showAvatar>
            <MarkdownRenderer content={b.text} />
            <OptionList options={b.options} onPick={onOption} disabled={i !== bubbles.length - 1} />
          </AssistantRow>
        );
      }
      const showAvatar = i === 0 || bubbles[i - 1].from !== "assistant";
      return (
        <AssistantRow key={i} avatar={avatar} showAvatar={showAvatar}>
          {b.steps && b.steps.length > 0 && <AgentStepTimeline steps={b.steps} />}
          {b.text && <MarkdownRenderer content={b.text} />}
          {b.summary && <ProfileSummaryCard values={b.summary} />}
          {b.details && <BrandDetailsSummaryCard details={b.details} />}
          {b.team && b.team.length > 0 && <TeamCards agents={b.team} onChat={onChatAgent} onEdit={onEditAgent} />}
        </AssistantRow>
      );
    })}
  </>
  );
};

/** The assistant's turn while it is still working: the logo animates. */
export const LiveAssistantTurn: React.FC<{
  avatar: React.ReactNode;
  showHeader: boolean;
  steps: AgentStep[];
  streaming: string;
}> = ({ avatar, showHeader, steps, streaming }) => (
  <div role="status" aria-live="polite">
    <AssistantRow avatar={avatar} showAvatar={showHeader} working>
      {steps.length > 0 && <AgentStepTimeline steps={steps} />}
      {streaming ? (
        <MarkdownRenderer content={streaming} isStreaming />
      ) : (
        steps.length === 0 && <span className="text-[14px] text-theme-fg-muted">Thinking…</span>
      )}
    </AssistantRow>
  </div>
);

/** Where the page docks the composer, like agent chat's bottom bar. */
export const SETUP_COMPOSER_SLOT_ID = "setup-composer-slot";

/** The dock the page renders once; agent chat's composer brings its own
 *  frosted bottom-bar shell, so this is only the mount point. */
export const SetupComposerDock: React.FC = () => (
  <div id={SETUP_COMPOSER_SLOT_ID} className="flex-shrink-0 empty:hidden" />
);

const noop = () => undefined;

/**
 * The composer IS agent chat's ChatInputArea, so the two can never drift.
 * `minimal` hides the "+" menu (files, media, tools): nothing to act on before
 * a Brand exists. Inline under the opening message, docked once the chat starts.
 */
export const SetupComposer: React.FC<{
  onSubmit: (message: string) => void;
  disabled?: boolean;
  docked?: boolean;
  inputRef?: React.Ref<HTMLTextAreaElement>;
}> = ({ onSubmit, disabled, docked = true, inputRef }) => {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const attachmentMenuRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setSlot(document.getElementById(SETUP_COMPOSER_SLOT_ID));
  }, []);
  // Agent chat clears its composer from the parent after a send; so do we.
  const composerControlRef = useRef<ChatComposerHandle>(null);

  const composer = (
    <ChatInputArea
      composerControlRef={composerControlRef}
      onSubmit={(message) => {
        composerControlRef.current?.setValue("");
        onSubmit(message);
      }}
      onMyLibrary={noop}
      onGallery={noop}
      onFileSelect={noop}
      selectedImages={[]}
      onRemoveImage={noop}
      isLoading={!!disabled}
      showAttachmentMenu={false}
      onToggleAttachmentMenu={noop}
      attachmentMenuRef={attachmentMenuRef}
      fileInputRef={fileInputRef}
      composerTextareaRef={inputRef}
      agentType="onboarding"
      variant={docked ? "docked" : "inline"}
      minimal
    />
  );

  return docked && slot ? createPortal(composer, slot) : composer;
};
